import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import {
    CalendarMonth as CalendarIcon,
    Link as LinkIcon,
    HowToVote as VoteIcon,
    Groups as GroupsIcon,
    Lightbulb as SmartIcon,
    FlashOn as FastIcon,
    Favorite as EasyIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// ── Hooks ─────────────────────────────────────────────────────────────────────
const useScrollY = () => {
    const [scrollY, setScrollY] = useState(0);
    useEffect(() => {
        const fn = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);
    return scrollY;
};

const useInView = (threshold = 0.12) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
};

const FadeUp = ({ children, delay = 0, distance = 48 }) => {
    const [ref, inView] = useInView();
    return (
        <Box ref={ref} sx={{
            opacity:   inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : `translateY(${distance}px)`,
            transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
            willChange: 'opacity, transform',
        }}>
            {children}
        </Box>
    );
};

// ── Landing ───────────────────────────────────────────────────────────────────
const Landing = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const scrollY  = useScrollY();

    const handleGetStarted = () => navigate(user ? '/dashboard' : '/register');

    return (
        <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', overflowX: 'hidden' }}>

            {/* ── Nav ──────────────────────────────────────────────────────── */}
            <Box sx={{
                px: { xs: 3, md: 5 }, py: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                bgcolor: '#FFFFFF', borderBottom: '1px solid rgba(0,0,0,0.06)',
                position: 'sticky', top: 0, zIndex: 100,
                boxShadow: '0 1px 12px rgba(108,92,231,0.06)',
            }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#7C3AED', letterSpacing: 0.5 }}>
                    Quuip
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {user ? (
                        <Button
                            variant="contained"
                            onClick={() => navigate('/dashboard')}
                            sx={{ background: 'linear-gradient(135deg,#7C3AED,#4F46E5)', borderRadius: 2, fontWeight: 700 }}
                        >
                            Go to Dashboard
                        </Button>
                    ) : (
                        <>
                            <Button onClick={() => navigate('/login')} sx={{ color: '#475569', fontWeight: 600 }}>Log in</Button>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/register')}
                                sx={{ background: 'linear-gradient(135deg,#7C3AED,#4F46E5)', borderRadius: 2, fontWeight: 700 }}
                            >
                                Sign up free
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {/* ── Hero ─────────────────────────────────────────────────────── */}
            <Box sx={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                pt: { xs: 10, md: 16 },
                pb: { xs: 12, md: 20 },
                textAlign: 'center',
                px: 2,
                position: 'relative',
                overflow: 'clip',
            }}>
                {/* Parallax orbs */}
                <Box sx={{ position:'absolute', top:-80, left:'3%', width:520, height:520, borderRadius:'50%', background:'rgba(255,255,255,0.07)', transform:`translateY(${scrollY*0.3}px)`, willChange:'transform', pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', top:40, right:'2%', width:380, height:380, borderRadius:'50%', background:'rgba(255,255,255,0.05)', transform:`translateY(${scrollY*0.5}px)`, willChange:'transform', pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', bottom:0, left:'38%', width:220, height:220, borderRadius:'50%', background:'rgba(255,255,255,0.04)', transform:`translateY(${scrollY*0.65}px)`, willChange:'transform', pointerEvents:'none' }} />

                <Container maxWidth="md" sx={{ position:'relative', zIndex:1 }}>

                    {/* Headline */}
                    <Box sx={{ transform:`translateY(${scrollY * -0.18}px)`, willChange:'transform' }}>
                        <Typography variant="h2" sx={{
                            fontWeight: 800, color: '#FFFFFF', mb: 2,
                            fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.15,
                            letterSpacing: -0.5,
                        }}>
                            The simplest way to plan{' '}
                            <Box component="span" sx={{ color: '#C4B5FD' }}>together</Box>
                            {' '}and{' '}
                            <Box component="span" sx={{ color: '#A5B4FC' }}>choose</Box>
                            {' '}faster.
                        </Typography>
                    </Box>

                    {/* Subtitle */}
                    <Box sx={{ transform:`translateY(${scrollY * -0.26}px)`, willChange:'transform' }}>
                        <Typography variant="h6" sx={{ color:'rgba(255,255,255,0.8)', mb: 6, fontWeight: 400, maxWidth: 520, mx: 'auto', lineHeight: 1.6 }}>
                            Create an event, share a link, and let everyone vote. We'll find the best time or option — instantly.
                        </Typography>
                    </Box>

                    {/* Pills + CTA */}
                    <Box sx={{ transform:`translateY(${scrollY * -0.34}px)`, willChange:'transform' }}>
                        <Box sx={{ display:'flex', justifyContent:'center', gap:{ xs:1.5, md:3 }, mb: 7, flexWrap:'wrap' }}>
                            {[
                                { icon: <CalendarIcon sx={{ fontSize:18, color:'#C4B5FD' }} />, text: 'Create an event in seconds' },
                                { icon: <LinkIcon      sx={{ fontSize:18, color:'#C4B5FD' }} />, text: 'Share your link with the group' },
                                { icon: <VoteIcon      sx={{ fontSize:18, color:'#C4B5FD' }} />, text: 'Everyone votes, we pick the best' },
                            ].map((item, i) => (
                                <Box key={i} sx={{
                                    display:'flex', alignItems:'center', gap:1,
                                    bgcolor:'rgba(255,255,255,0.12)',
                                    border:'1px solid rgba(255,255,255,0.2)',
                                    borderRadius: 2.5, px: 2.5, py: 1,
                                    backdropFilter: 'blur(6px)',
                                }}>
                                    {item.icon}
                                    <Typography variant="body2" sx={{ color:'rgba(255,255,255,0.9)', fontWeight: 600, fontSize:'0.85rem' }}>
                                        {item.text}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:2, flexWrap:'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleGetStarted}
                                sx={{
                                    bgcolor:'#FFFFFF', color:'#7C3AED',
                                    fontWeight: 800, px: 5, py: 1.6,
                                    borderRadius: 3, fontSize: '1rem',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        bgcolor: '#F5F3FF',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
                                    },
                                }}
                            >
                                Get Started Free →
                            </Button>
                            <Typography variant="body2" sx={{ color:'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                                No signup required to vote
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* ── How it works ─────────────────────────────────────────────── */}
            <Container maxWidth="lg" sx={{ py: { xs: 10, md: 14 } }}>
                <FadeUp delay={0}>
                    <Box sx={{ textAlign:'center', mb: 8 }}>
                        <Typography variant="overline" sx={{ color:'#7C3AED', letterSpacing: 2, fontWeight: 700, fontSize:'0.72rem' }}>
                            HOW IT WORKS
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color:'#0F172A', mt: 1 }}>
                            Three steps, zero back-and-forth.
                        </Typography>
                    </Box>
                </FadeUp>

                <Box sx={{ display:'flex', gap:3, flexDirection:{ xs:'column', md:'row' } }}>
                    {[
                        { step:'01', icon: <CalendarIcon sx={{ fontSize:32, color:'#7C3AED' }} />, title:'Create your event', body:'Give it a name, propose a few time options or open-ended choices, and you\'re ready in under a minute.' },
                        { step:'02', icon: <LinkIcon      sx={{ fontSize:32, color:'#7C3AED' }} />, title:'Share one link',     body:'Send your unique link to the group. No app installs, no accounts required — anyone can vote.' },
                        { step:'03', icon: <VoteIcon      sx={{ fontSize:32, color:'#7C3AED' }} />, title:'We pick the best',   body:'Everyone votes, and Quuip surfaces the option that works for the most people. Done.' },
                    ].map((item, i) => (
                        <FadeUp key={i} delay={i * 0.12}>
                            <Paper elevation={0} variant="outlined" sx={{
                                p: 4, borderRadius: 4,
                                borderColor: 'rgba(0,0,0,0.08)',
                                bgcolor: '#FFFFFF',
                                flex: 1,
                                transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                                '&:hover': {
                                    transform: 'translateY(-6px)',
                                    boxShadow: '0 12px 32px rgba(124,58,237,0.1)',
                                    borderColor: '#C4B5FD',
                                },
                            }}>
                                <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:3 }}>
                                    <Box sx={{ width:44, height:44, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                        {item.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color:'#7C3AED', fontSize:'0.8rem', letterSpacing:1.5 }}>
                                        STEP {item.step}
                                    </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color:'#0F172A', mb: 1.5 }}>
                                    {item.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color:'#64748B', lineHeight: 1.7 }}>
                                    {item.body}
                                </Typography>
                            </Paper>
                        </FadeUp>
                    ))}
                </Box>
            </Container>

            {/* ── Why Quuip ───────────────────────────────────────────────── */}
            <Box sx={{ bgcolor:'#F1F5F9', py:{ xs:10, md:14 } }}>
                <Container maxWidth="lg">
                    <FadeUp delay={0}>
                        <Box sx={{ textAlign:'center', mb: 8 }}>
                            <Typography variant="overline" sx={{ color:'#7C3AED', letterSpacing: 2, fontWeight: 700, fontSize:'0.72rem' }}>
                                WHY QUUIP
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color:'#0F172A', mt: 1 }}>
                                Convene with anyone.{' '}
                                <Box component="span" sx={{ color:'#7C3AED' }}>Decide together.</Box>
                            </Typography>
                            <Typography variant="body1" sx={{ color:'#475569', maxWidth: 460, mx:'auto', mt: 1.5 }}>
                                Group plans shouldn't be a mess. Quuip makes it effortless.
                            </Typography>
                        </Box>
                    </FadeUp>

                    <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', sm:'1fr 1fr', md:'repeat(4,1fr)' }, gap:3 }}>
                        {[
                            { icon: <GroupsIcon sx={{ fontSize:36, color:'#7C3AED' }} />, title:'Group Focused',      body:'Built for groups, not just individuals.' },
                            { icon: <SmartIcon  sx={{ fontSize:36, color:'#7C3AED' }} />, title:'Smart Decisions',    body:"We find the best option with everyone's input." },
                            { icon: <FastIcon   sx={{ fontSize:36, color:'#7C3AED' }} />, title:'No Back-and-Forth', body:'One link. Less chat. Faster plans.' },
                            { icon: <EasyIcon   sx={{ fontSize:36, color:'#7C3AED' }} />, title:'Actually Easy',      body:'Clean, fast, and delightful to use.' },
                        ].map((item, i) => (
                            <FadeUp key={i} delay={i * 0.1}>
                                <Paper elevation={0} variant="outlined" sx={{
                                    p: 4, textAlign:'center', borderRadius: 4,
                                    borderColor: 'rgba(0,0,0,0.08)',
                                    bgcolor: '#FFFFFF',
                                    height: '100%',
                                    transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: '0 12px 32px rgba(124,58,237,0.1)',
                                        borderColor: '#C4B5FD',
                                    },
                                }}>
                                    <Box sx={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2.5 }}>
                                        {item.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color:'#0F172A', mb: 1 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color:'#64748B', lineHeight: 1.7 }}>
                                        {item.body}
                                    </Typography>
                                </Paper>
                            </FadeUp>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* ── Footer CTA ───────────────────────────────────────────────── */}
            <FadeUp delay={0} distance={60}>
                <Box sx={{
                    py: { xs: 10, md: 14 }, textAlign:'center',
                    background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                    position:'relative', overflow:'clip',
                }}>
                    <Box sx={{ position:'absolute', top:-80, left:'-5%', width:360, height:360, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }} />
                    <Box sx={{ position:'absolute', bottom:-60, right:'5%', width:280, height:280, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }} />

                    <Container maxWidth="sm" sx={{ position:'relative', zIndex:1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, color:'#FFFFFF', mb: 2, letterSpacing:-0.5 }}>
                            Ready to stop the scheduling chaos?
                        </Typography>
                        <Typography variant="body1" sx={{ color:'rgba(255,255,255,0.75)', mb: 5, lineHeight: 1.7 }}>
                            Create your first event in under a minute. Free forever for groups.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleGetStarted}
                            sx={{
                                bgcolor:'#FFFFFF', color:'#7C3AED',
                                fontWeight: 800, px: 6, py: 1.8,
                                borderRadius: 3, fontSize: '1rem',
                                boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#F5F3FF',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 14px 36px rgba(0,0,0,0.22)',
                                },
                            }}
                        >
                            Get Started Free →
                        </Button>
                    </Container>
                </Box>
            </FadeUp>

        </Box>
    );
};

export default Landing;
